import { useEffect, useState, useCallback } from "react";
import { ChevronLeft, Loader2, ChevronDown, ChevronUp, Check, AlertCircle, Plus, Trash2, Eye, X, Clipboard } from "lucide-react";
import toast from "react-hot-toast";
import type { AdminOrderListItem, OrderDetailsResponse } from "./types";
import { AdminOrderManagementService } from "../../services/api/admin-order.service";
import { AdminOrderService } from "../../services/api/AdminOrderService/admin.order.service";
import OrderDetailsHeader from "./OrderDetailsHeader";
import PrescriptionList from "./PrescriptionList";
import RejectOrderSection from "./RejectOrderSection";
import { Product, SelectedProduct, StockItem } from "../Inventory-Stocks & Orders/Clinic/ClinicOrder/OfflineOrder/types";
import medicineIcon from "../../assets/images/medicineIcon.png";
import defaultProduct from "../../assets/images/default_product.jpg";
import { firestore as db } from "../../firebase/firebase";
import { collection, doc, setDoc } from "firebase/firestore";
import CancelOrderModal from "./CancelOrderModal";

interface PrepareOrderProps {
  order: AdminOrderListItem;
  onBack: () => void;
}

interface DoctorInfoState {
  doctorName: string;
  doctorRegistrationNumber: string;
}

const PrepareOrder: React.FC<PrepareOrderProps> = ({ order, onBack }) => {
  const [orderDetails, setOrderDetails] = useState<OrderDetailsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // Collapsible States - Closed by default
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [showPrescriptions, setShowPrescriptions] = useState(false);
  const [showDoctorInfo, setShowDoctorInfo] = useState(false);

  // Products State - We keep a unified list of admin-added products.
  const [adminProducts, setAdminProducts] = useState<SelectedProduct[]>([]);

  // Add Medicine Modal State
  const [showModal, setShowModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalItemsCount: 0,
    totalPages: 1,
    nextPage: null as number | null,
    previousPage: null as number | null,
  });

  // Doctor Form State
  const [doctorInfo, setDoctorInfo] = useState<DoctorInfoState>({
    doctorName: "",
    doctorRegistrationNumber: "",
  });

  // Note Modal State
  const [noteModalProduct, setNoteModalProduct] = useState<SelectedProduct | null>(null);
  const [noteText, setNoteText] = useState("");

  // Rejection State
  const [actionState, setActionState] = useState<"none" | "rejecting">("none");
  const [rejectingApiCall, setRejectingApiCall] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, [order.orderId]);

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await AdminOrderManagementService.getOrderDetails(order.orderId);
      setOrderDetails(response);
    } catch (error) {
      console.error("Error fetching order details:", error);
      toast.error("Failed to load order details");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Clinic stock for the modal
  const fetchProducts = useCallback(
    async (searchTerm = "", page = 1, itemsPerPage = 10) => {
      if (!orderDetails?.clinicId) return;
      try {
        setModalLoading(true);
        const response = await AdminOrderService.getClinicProductListing(
          page,
          itemsPerPage,
          searchTerm,
          orderDetails.clinicId
        );
        if (response?.data?.data && Array.isArray(response.data.data)) {
          const transformedProducts = response.data.data.map((item: StockItem) => ({
            compositeId: `admin_${item.id}_${item.product_id}`,
            id: item.id,
            product_id: item.product_id,
            product_name: item.product?.name || "Unknown Product",
            manufacturer_name: item.manufacturer?.manufacturer_name || "Unknown Manufacturer",
            product_image: item.product?.productImage || medicineIcon,
            landing_cost: parseFloat(item.landing_cost) || 0,
            mrp: parseFloat(item.productMrp || item.mrp),
            quantity: item.remaining_stocks || 0, // Strips count
            expiry_date: item.expiry_date,
            batch_number: item.batch_number,
            hsn_number: item.hsn_number,
            product_rate: parseFloat(item.product_rate) || 0,
            discount: parseFloat(item.sellingDiscount) || 0,
            cgst: parseFloat(item.cgst) || 0,
            sgst: parseFloat(item.sgst) || 0,
            free_items: item.free_items || 0,
            finalSP: parseFloat(item.finalSP) || 0, // Full strip finalSP
            sellingDiscount: parseFloat(item.sellingDiscount) || 0, // Autofilled selling discount
            remainingStocks: item.remaining_stocks,
            totalStock: item.remaining_stocks, // Limit to strips
            noOfItemsInPack: item?.product?.tablet_per_stripe || 10, // Default to 10
            perPackPrice: parseFloat(item.productMrp || item.mrp), // Full pack MRP
            expiry: item.expiry_date,
            batch: item.batch_number,
            salt: item.salt,
          }));
          setAvailableProducts(transformedProducts);
          setPagination(
            response.data.pagination || {
              currentPage: 1,
              itemsPerPage: 10,
              totalItemsCount: 0,
              totalPages: 1,
              nextPage: null,
              previousPage: null,
            }
          );
        }
      } catch (err) {
        toast.error("Failed to load clinic stock medicines");
      } finally {
        setModalLoading(false);
      }
    },
    [orderDetails?.clinicId]
  );

  // Debounced product search inside the modal
  useEffect(() => {
    if (!showModal) return;
    const timer = setTimeout(() => {
      fetchProducts(searchText, 1, pagination.itemsPerPage);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText, showModal, fetchProducts, pagination.itemsPerPage]);

  const handleModalSearch = (value: string) => {
    setSearchText(value);
  };

  const handleAddProduct = (product: Product) => {
    const existing = adminProducts.find((p) => p.compositeId === product.compositeId);
    if (existing) return;

    if (product.totalStock <= 0) {
      toast.error("Product is out of stock!");
      return;
    }

    setAdminProducts((prev) => [...prev, { ...product, selectedQuantity: 0, substituteNote: "" }]);
    setSearchText("");
    toast.success(`Added "${product.product_name}" to Admin List`);
  };

  const handleRemoveProduct = (compositeId: string) => {
    setAdminProducts((prev) => prev.filter((p) => p.compositeId !== compositeId));
  };

  const onUpdateDiscount = (compositeId: string, discount: number) => {
    const product = adminProducts.find((p) => p.compositeId === compositeId);
    if (!product) return;

    if (isNaN(discount)) discount = 0;

    // Validation: Selling Price should not be less than landing cost
    const currentPrice = product.perPackPrice * (1 - discount / 100);

    if (currentPrice < product.landing_cost) {
      toast.error(`Price (₹${currentPrice.toFixed(2)}) cannot be less than landing cost (₹${product.landing_cost.toFixed(2)})`);
      return;
    }

    setAdminProducts((prev) =>
      prev.map((p) => (p.compositeId === compositeId ? { ...p, sellingDiscount: discount } : p))
    );
  };

  const getDaysRemaining = (expiryDate: string) => {
    if (!expiryDate) return null;
    const today = new Date();
    const expiry = new Date(expiryDate);
    today.setHours(0, 0, 0, 0);
    expiry.setHours(0, 0, 0, 0);
    const diffTime = expiry.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Rejection/Cancellation handler
  const handleReject = async (reason: string) => {
    try {
      setRejectingApiCall(true);
      const res = await AdminOrderManagementService.cancelOrder({
        orderId: order.orderId,
        cancelledBy: "ADMIN",
        cancelationReason: reason,
      });
      if (res.success) {
        toast.success("Order cancelled successfully!");
        onBack();
      } else {
        toast.error(res.message || "Failed to cancel order");
      }
    } catch (error) {
      toast.error("Failed to cancel order");
    } finally {
      setRejectingApiCall(false);
      setActionState("none");
    }
  };

  // ── Pricing Calculations ─────────────────────────────────
  // Patient Products
  const patientProducts = orderDetails?.products || [];
  const patientSubtotal = patientProducts.reduce((sum, p) => sum + parseFloat(p.productPrice) * p.quantity, 0);
  const patientMrpTotal = patientProducts.reduce((sum, p) => sum + parseFloat(p.productMrp) * p.quantity, 0);

  // Admin Products
  const adminSubtotal = adminProducts.reduce(
    (sum, p) => sum + p.perPackPrice * (1 - (p.sellingDiscount || 0) / 100) * p.selectedQuantity,
    0
  );
  const adminMrpTotal = adminProducts.reduce((sum, p) => sum + p.perPackPrice * p.selectedQuantity, 0);
  const adminInitialDiscountTotal = adminProducts.reduce(
    (sum, p) => sum + (p.perPackPrice * ((p.sellingDiscount || 0) / 100)) * p.selectedQuantity,
    0
  );

  // Order Totals
  const totalMRP = patientMrpTotal + adminMrpTotal;
  const subtotal = patientSubtotal + adminSubtotal;
  const roundedTotal = Math.round(subtotal);
  const roundOff = parseFloat((roundedTotal - subtotal).toFixed(2));
  const totalSavings = (patientMrpTotal - patientSubtotal) + adminInitialDiscountTotal;

  // Prepare Submission Payload
  const handlePrepareOrderSubmit = async () => {
    if (!orderDetails) return;

    // Validation: Admin products must not have 0 quantity
    if (adminProducts.some((p) => !p.selectedQuantity || p.selectedQuantity === 0)) {
      toast.error("Admin-added products cannot have a quantity of 0. Please update or remove them.");
      return;
    }

    try {
      setIsProcessing(true);

      // ── Doctor Validation Check ─────────────────────────────
      // Before preparing order, check if doctorName is required for any product
      const adminCheckPromises = adminProducts.map(async (p) => {
        try {
          const res = await AdminOrderManagementService.checkProduct(p.product_id);
          return res.success && res.doctorNameReq;
        } catch {
          return false;
        }
      });

      const allChecks = await Promise.all(adminCheckPromises);
      const doctorNameRequired = allChecks.some((required) => required === true);

      if (doctorNameRequired && !doctorInfo.doctorName.trim()) {
        toast.error("Doctor's Name is mandatory for this order as some products require doctor validation.");
        setIsProcessing(false);
        // Automatically open the doctor details section so the user sees it
        setShowDoctorInfo(true);
        return;
      }

      // Map admin products to payload
      const mappedAdminProducts = adminProducts.map((p) => {
        const finalUnitPrice = p.perPackPrice * (1 - (p.sellingDiscount || 0) / 100);

        return {
          productId: p.product_id,
          numberOfItems: p.selectedQuantity,
          price: parseFloat(finalUnitPrice.toFixed(2)),
          mrp: p.mrp,
          inwardId: p.id || "",
          substituteNote: p.substituteNote || "",
        };
      });

      const payload = {
        orderId: orderDetails.orderId,
        orderTotal: roundedTotal,
        products: [...mappedAdminProducts], // Submit prepared admin-added products
        discount: 0,
        doctorName: doctorInfo.doctorName.trim() || undefined,
        doctorRegistrationNumber: doctorInfo.doctorRegistrationNumber.trim() || undefined,
      };

      const res = await AdminOrderManagementService.prepareOrder(payload);

      if (res.success) {
        toast.success("Order prepared successfully!");
        await addNotificationInFirebase();
        onBack();
      } else {
        toast.error(res.message || "Failed to prepare order");
      }
    } catch (err) {
      console.error("Error preparing order:", err);
      toast.error("An error occurred while preparing the order.");
    } finally {
      setIsProcessing(false);
    }
  };

  const addNotificationInFirebase = async () => {
    if (!orderDetails) return;
    const docRef = doc(
      collection(db, "paymentCompletion"),
      String(orderDetails.orderId)
    );
    await setDoc(
      docRef,
      {
        message:
          "Your order has been prepared. You are required to complete payment for further processing.",
        title: "Order Prepared by admin",
        toId: orderDetails?.patientId || "",
        status: "Unread",
        order_number: orderDetails?.orderNumber || "",
        order_date: orderDetails?.orderDate || "",
      },
      { merge: true }
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 size={32} className="animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!orderDetails) {
    return (
      <div className="p-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={16} /> Back to Orders
        </button>
        <p className="text-sm text-red-500">Failed to load order details.</p>
      </div>
    );
  }

  return (
    <div className="h-full bg-slate-50 flex flex-col overflow-hidden">
      <div className="flex-1 overflow-auto p-5">
        <button onClick={onBack} className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 bg-transparent border-none cursor-pointer mb-4 hover:text-slate-700">
          <ChevronLeft size={16} /> Back to Orders
        </button>

        <div className="flex gap-5">
          {/* Main Content Area */}
          <div className="flex-1 min-w-0">

            {/* 1. Collapsible Order Details Header */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowOrderDetails(!showOrderDetails)}
              >
                <span className="uppercase tracking-wider">Order Details ({orderDetails.orderNumber})</span>
                {showOrderDetails ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showOrderDetails && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <OrderDetailsHeader order={orderDetails} onUpdateSuccess={fetchOrderDetails} />
                </div>
              )}
            </div>

            {/* 2. Collapsible Prescriptions */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowPrescriptions(!showPrescriptions)}
              >
                <span className="uppercase tracking-wider">Uploaded Prescriptions</span>
                {showPrescriptions ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showPrescriptions && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <PrescriptionList
                    prescriptions={orderDetails.prescriptions}
                    orderId={orderDetails.orderId}
                    orderDetails={orderDetails}
                    onUploadSuccess={fetchOrderDetails}
                  />
                </div>
              )}
            </div>

            {/* 3. Collapsible Doctor Information */}
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden mb-4 shadow-sm">
              <button
                className="w-full px-5 py-3.5 flex items-center justify-between font-bold text-xs text-slate-700 hover:bg-slate-50 transition-colors border-none bg-transparent cursor-pointer"
                onClick={() => setShowDoctorInfo(!showDoctorInfo)}
              >
                <span className="uppercase tracking-wider">Doctor Information (Optional)</span>
                {showDoctorInfo ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {showDoctorInfo && (
                <div className="px-5 pb-5 border-t border-slate-100 pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Name</label>
                      <input
                        type="text"
                        value={doctorInfo.doctorName}
                        onChange={(e) => setDoctorInfo((d) => ({ ...d, doctorName: e.target.value }))}
                        placeholder="Enter doctor's name"
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                      />
                    </div>
                    <div>
                      <label className="text-[0.68rem] font-semibold text-slate-500 block mb-1">Doctor Registration Number</label>
                      <input
                        type="text"
                        value={doctorInfo.doctorRegistrationNumber}
                        onChange={(e) => setDoctorInfo((d) => ({ ...d, doctorRegistrationNumber: e.target.value }))}
                        placeholder="Enter registration number"
                        className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-all bg-slate-50/50"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 4. Products Table (Patient & Admin Added) */}
            <div className="bg-white rounded-xl border border-slate-200 p-5 mb-4 shadow-sm flex flex-col min-h-[450px]">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Ordered & Selected Medicines</h3>
                <button
                  onClick={() => setShowModal(true)}
                  className="px-3.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-100 transition-all flex items-center gap-1.5 cursor-pointer"
                >
                  <Plus size={14} /> Add Medicine
                </button>
              </div>

              {/* Table Header */}
              <div className="grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.6fr] gap-2 text-[10px] font-bold text-slate-400 uppercase border-b border-slate-100 pb-2 mb-2 w-full pr-2">
                <div>Name</div>
                <div>MRP</div>
                <div>Price (finalSP)</div>
                <div className="text-center">Quantity</div>
                <div>Discount (%)</div>
                <div>Total</div>
                <div className="text-center">Action</div>
              </div>

              {/* Combined Table Rows */}
              {patientProducts.length === 0 && adminProducts.length === 0 ? (
                <div className="flex-1 flex items-center justify-center py-12 text-slate-400 text-xs">
                  No medicines in this order yet. Click <b>"Add Medicine"</b> to add clinic stocks.
                </div>
              ) : (
                <div className="flex-1 overflow-y-auto w-full pr-2 max-h-[400px]">

                  {/* Render Patient Products (Read-Only) */}
                  {patientProducts.map((product) => {
                    const priceNum = parseFloat(product.productPrice) || 0;
                    const mrpNum = parseFloat(product.productMrp) || 0;
                    const finalTotal = priceNum * product.quantity;
                    const calculatedDiscount = mrpNum > 0 ? ((mrpNum - priceNum) / mrpNum) * 100 : 0;

                    return (
                      <div
                        key={`patient_${product.productId}`}
                        className="grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.6fr] gap-2 items-center py-3 border-b border-slate-50 bg-slate-50/30 hover:bg-slate-50 transition-colors w-full rounded-lg px-1 mb-1.5"
                      >
                        {/* Name */}
                        <div className="flex flex-col min-w-0 pr-2">
                          <div className="text-[12px] font-bold text-slate-700 truncate" title={product.name}>
                            {product.name}
                          </div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className="px-1.5 py-0.5 rounded text-[8px] font-extrabold bg-blue-50 text-blue-600 border border-blue-100 uppercase">
                              Patient
                            </span>
                          </div>
                        </div>

                        {/* MRP */}
                        <div className="text-[11px] text-slate-600 font-bold">
                          ₹ {mrpNum.toFixed(2)}
                        </div>

                        {/* Price */}
                        <div className="text-[11px] text-slate-600 font-semibold">
                          ₹ {priceNum.toFixed(2)}
                        </div>

                        {/* Quantity (Total Qty) */}
                        <div className="text-center text-[11px] text-slate-700 font-bold bg-slate-100/80 rounded px-1.5 py-0.5 w-max mx-auto">
                          {product.quantity}
                        </div>

                        {/* Discount */}
                        <div className="text-[11px] text-slate-500 font-semibold">
                          {calculatedDiscount > 0 ? `${calculatedDiscount.toFixed(1)}%` : "0%"}
                        </div>

                        {/* Total */}
                        <div className="text-[12px] font-extrabold text-slate-700">
                          ₹ {finalTotal.toFixed(2)}
                        </div>

                        {/* Action (N/A) */}
                        <div className="text-center text-[11px] text-slate-300 font-medium">-</div>
                      </div>
                    );
                  })}

                  {/* Render Admin Products (Editable) */}
                  {adminProducts.map((product) => {
                    const finalPrice =
                      product.perPackPrice *
                      (1 - (product.sellingDiscount || 0) / 100) *
                      product.selectedQuantity;
                    const unitPrice = product.perPackPrice * (1 - (product.sellingDiscount || 0) / 100);

                    return (
                      <div
                        key={product.compositeId}
                        className="grid grid-cols-[3fr_1.2fr_1.2fr_1.2fr_1.2fr_1.2fr_0.6fr] gap-2 items-center py-3 border-b border-emerald-50/50 bg-emerald-50/5 hover:bg-emerald-50/20 transition-colors w-full rounded-lg px-1 mb-1.5"
                      >
                        {/* Name */}
                        <div className="flex flex-col min-w-0 pr-2">
                          <div className="text-[12px] font-bold text-slate-700 truncate" title={product.product_name}>
                            {product.product_name}
                          </div>
                          {product.substituteNote ? (
                            <div
                              onClick={() => {
                                setNoteModalProduct(product);
                                setNoteText(product.substituteNote || "");
                              }}
                              className="text-[9px] text-emerald-600 font-bold mt-1 bg-emerald-50 hover:bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-200 w-max truncate max-w-[200px] cursor-pointer transition-colors"
                              title="Click to edit substitute note"
                            >
                              Note: {product.substituteNote}
                            </div>
                          ) : (
                            <button
                              onClick={() => {
                                setNoteModalProduct(product);
                                setNoteText("");
                              }}
                              className="text-[9px] text-slate-500 font-bold mt-1 bg-slate-100 hover:bg-slate-200 px-1.5 py-0.5 rounded border border-slate-200 w-max cursor-pointer transition-colors"
                            >
                              + Add Note
                            </button>
                          )}
                          <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400 truncate">
                            <span className="truncate">{product.manufacturer_name}</span>
                            {product.batch_number && <span className="font-medium bg-slate-100 px-1 rounded flex-shrink-0">Batch: {product.batch_number}</span>}
                          </div>
                        </div>

                        {/* MRP */}
                        <div className="text-[11px] text-slate-600 font-bold whitespace-nowrap">
                          ₹ {product.mrp?.toFixed(2)}
                        </div>

                        {/* Price (finalSP) */}
                        <div className="text-[11px] text-slate-600 font-semibold whitespace-nowrap">
                          ₹ {unitPrice.toFixed(2)}
                        </div>

                        {/* Quantity Input (Strips only) */}
                        <div className="flex justify-center px-1">
                          <input
                            type="text"
                            inputMode="numeric"
                            value={product.selectedQuantity || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || /^\d*$/.test(val)) {
                                const qty = val === "" ? 0 : parseInt(val, 10);
                                if (qty > product.totalStock) {
                                  toast.error(`Stock unavailable! Only ${product.totalStock} items left`);
                                  return;
                                }
                                setAdminProducts((prev) =>
                                  prev.map((p) =>
                                    p.compositeId === product.compositeId ? { ...p, selectedQuantity: qty } : p
                                  )
                                );
                              }
                            }}
                            className="w-12 px-1 py-0.5 text-center border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 placeholder-transparent bg-white shadow-sm font-semibold"
                          />
                        </div>

                        {/* Discount */}
                        <div className="px-1">
                          <input
                            type="text"
                            inputMode="decimal"
                            value={product.sellingDiscount === 0 ? "0" : product.sellingDiscount || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "" || /^(\d+\.?\d*|\.\d+)$/.test(val)) {
                                onUpdateDiscount(product.compositeId, parseFloat(val) || 0);
                              }
                            }}
                            className="w-full px-2 py-0.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500 bg-white font-semibold text-slate-700"
                          />
                        </div>

                        {/* Total */}
                        <div className="text-[12px] font-extrabold text-emerald-600">
                          ₹ {finalPrice.toFixed(2)}
                        </div>

                        {/* Action - Add Note + Remove Product */}
                        <div className="flex justify-center items-center gap-1.5">
                          <button
                            onClick={() => {
                              setNoteModalProduct(product);
                              setNoteText(product.substituteNote || "");
                            }}
                            className={`p-1 rounded cursor-pointer border-none bg-transparent hover:bg-slate-100 transition-colors ${product.substituteNote ? "text-emerald-600 font-extrabold" : "text-slate-400"
                              }`}
                            title={product.substituteNote ? "Edit substitute note" : "Add substitute note"}
                          >
                            <Clipboard size={14} />
                          </button>
                          <button
                            onClick={() => handleRemoveProduct(product.compositeId)}
                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer border-none bg-transparent"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar - Calculations & Prepare/Cancel Submission */}
          <div className="w-[300px] flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm sticky top-5 flex flex-col gap-4">
              {/* Order Note (if present) */}
              {(orderDetails?.ordernote || (orderDetails as any)?.orderNote) && (
                <div className="p-3 bg-amber-50/80 border border-amber-200 rounded-xl">
                  <div className="flex items-center gap-1.5 mb-1 text-amber-700">
                    <Clipboard size={14} className="flex-shrink-0" />
                    <span className="text-[11px] font-bold uppercase tracking-wider">Order Note</span>
                  </div>
                  <p className="text-xs text-amber-900 leading-relaxed font-medium">
                    {orderDetails?.ordernote || (orderDetails as any)?.orderNote}
                  </p>
                </div>
              )}

              <div>
                <h4 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-2">Order Summary</h4>
                <div className="flex flex-col gap-2 max-h-[180px] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>

                  {/* Patient Products in Summary */}
                  {patientProducts.map((p) => (
                    <div key={`summary_patient_${p.productId}`} className="flex justify-between items-center text-xs">
                      <div className="min-w-0 pr-2">
                        <p className="font-bold text-slate-600 truncate">{p.name}</p>
                        <span className="text-[9px] font-bold bg-blue-50 text-blue-500 border border-blue-100 rounded px-1">
                          Patient × {p.quantity}
                        </span>
                      </div>
                      <span className="font-bold text-slate-700 whitespace-nowrap">
                        ₹{(parseFloat(p.productPrice) * p.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}

                  {/* Admin Products in Summary */}
                  {adminProducts.map((p) => {
                    const originalSub = p.perPackPrice * p.selectedQuantity;
                    const finalSub = p.perPackPrice * (1 - (p.sellingDiscount || 0) / 100) * p.selectedQuantity;
                    const hasDiscount = (p.sellingDiscount || 0) > 0;

                    return (
                      <div key={`summary_admin_${p.compositeId}`} className="flex justify-between items-center text-xs">
                        <div className="min-w-0 pr-2">
                          <p className="font-bold text-slate-600 truncate">{p.product_name}</p>
                          <span className="text-[9px] font-bold bg-emerald-50 text-emerald-500 border border-emerald-100 rounded px-1">
                            Admin × {p.selectedQuantity}
                          </span>
                        </div>
                        <div className="flex flex-col items-end whitespace-nowrap">
                          {hasDiscount && (
                            <span className="text-[10px] text-slate-400 line-through">
                              ₹{originalSub.toFixed(2)}
                            </span>
                          )}
                          <span className="font-bold text-emerald-600">
                            ₹{finalSub.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })}

                  {patientProducts.length === 0 && adminProducts.length === 0 && (
                    <p className="text-xs text-slate-400 italic">No products added yet.</p>
                  )}
                </div>
              </div>

              <hr className="border-slate-100 my-0.5" />

              {/* Costing Breakdowns */}
              <div className="flex flex-col gap-2.5 text-xs font-semibold text-slate-500">
                <div className="flex justify-between">
                  <span>Total MRP</span>
                  <span className="text-slate-700 font-bold">₹{totalMRP.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[11px] text-slate-400 pl-2">
                  <span>↳ Patient Products</span>
                  <span>₹{patientMrpTotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-[11px] text-slate-400 pl-2">
                  <span>↳ Admin Products</span>
                  <span>₹{adminMrpTotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-emerald-600 bg-emerald-50/50 px-2 py-1.5 rounded-lg border border-emerald-100/50">
                  <span className="font-bold">Total Savings</span>
                  <span className="font-extrabold">₹{totalSavings.toFixed(2)}</span>
                </div>

                {roundOff !== 0 && (
                  <div className="flex justify-between text-slate-400 text-[11px]">
                    <span>Round Off</span>
                    <span>{roundOff > 0 ? `+₹${roundOff}` : `-₹${Math.abs(roundOff)}`}</span>
                  </div>
                )}

                <hr className="border-slate-100 my-0.5" />

                <div className="flex justify-between items-center text-sm font-extrabold text-slate-800 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
                  <span>Total Payable</span>
                  <span className="text-lg font-black text-emerald-600">₹{roundedTotal}</span>
                </div>
              </div>

              {/* Actions Section */}
              <div className="flex flex-col gap-2 mt-2">
                <button
                  onClick={handlePrepareOrderSubmit}
                  disabled={isProcessing || actionState !== "none"}
                  className="w-full py-2.5 rounded-lg text-xs font-bold bg-gradient-to-r from-emerald-500 to-green-600 text-white hover:shadow-lg transition-all border-none cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Preparing...
                    </>
                  ) : (
                    <>
                      <Check size={14} /> Prepare Order
                    </>
                  )}
                </button>

                <button
                  onClick={() => setShowCancelModal(true)}
                  disabled={isProcessing}
                  className="w-full py-2.5 rounded-lg text-xs font-semibold bg-white border border-red-200 text-red-600 hover:bg-red-50 transition-all cursor-pointer disabled:opacity-40 flex items-center justify-center gap-1.5"
                >
                  <AlertCircle size={14} />
                  Cancel Order
                </button>

                {actionState === "rejecting" && (
                  <div className="mt-2">
                    <RejectOrderSection
                      onReject={handleReject}
                      rejecting={rejectingApiCall}
                      onCancel={() => setActionState("none")}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Add Note Modal (Popup) */}
      {noteModalProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setNoteModalProduct(null)}
          />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col overflow-hidden animate-in">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <h2 className="text-base font-bold text-gray-800">Add Note</h2>
              <button
                onClick={() => setNoteModalProduct(null)}
                className="p-1.5 hover:bg-gray-100 rounded-full transition-colors border-none bg-transparent cursor-pointer"
              >
                <X size={18} className="text-gray-500" />
              </button>
            </div>

            {/* Product row details header & box */}
            <div className="px-6 pt-5">
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                <div className="grid grid-cols-[2fr_1fr_1.2fr_0.8fr_1fr_1fr_0.6fr_0.6fr] gap-2 px-4 py-2 bg-gray-50 text-[10px] font-bold text-gray-500 border-b border-gray-200 uppercase">
                  <div>Name</div>
                  <div>Batch</div>
                  <div>Expiry</div>
                  <div>Qty/Pack</div>
                  <div>MRP</div>
                  <div>MRP/Unit</div>
                  <div className="text-center">Strips</div>
                  <div className="text-center">Tablets</div>
                </div>
                <div className="grid grid-cols-[2fr_1fr_1.2fr_0.8fr_1fr_1fr_0.6fr_0.6fr] gap-2 items-center px-4 py-3 text-xs font-semibold text-gray-700">
                  <div className="truncate font-bold text-slate-800">{noteModalProduct.product_name}</div>
                  <div className="truncate text-slate-500">{noteModalProduct.batch_number || "N/A"}</div>
                  <div className="text-slate-500">
                    {getDaysRemaining(noteModalProduct.expiry_date)
                      ? `${getDaysRemaining(noteModalProduct.expiry_date)} days left`
                      : "N/A"}
                  </div>
                  <div className="text-slate-500">{noteModalProduct.noOfItemsInPack || 1}</div>
                  <div className="text-slate-500">₹{noteModalProduct.perPackPrice?.toFixed(2)}</div>
                  <div className="text-slate-500">
                    ₹{(noteModalProduct.perPackPrice / (noteModalProduct.noOfItemsInPack || 1)).toFixed(2)}
                  </div>
                  <div className="text-center text-slate-700">{noteModalProduct.selectedQuantity}</div>
                  <div className="text-center text-slate-500">0</div>
                </div>
              </div>
            </div>

            {/* Note edit section */}
            <div className="p-6 flex flex-col gap-4">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="Enter replacement or substitution note (e.g. This medicine is a replacement for Betaloc 100mg...)"
                rows={4}
                className="w-full p-3 border border-gray-300 rounded-lg text-xs outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 shadow-sm resize-none font-semibold text-slate-700"
              />
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    setAdminProducts((prev) =>
                      prev.map((p) =>
                        p.compositeId === noteModalProduct.compositeId
                          ? { ...p, substituteNote: noteText }
                          : p
                      )
                    );
                    setNoteModalProduct(null);
                    toast.success("Substitute note saved successfully!");
                  }}
                  className="px-5 py-2.5 rounded-lg text-white text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 transition-colors border-none cursor-pointer"
                >
                  Add Note
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Split screen Add Medicines Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] md:max-w-6xl max-h-[90vh] flex flex-col overflow-hidden animate-in">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-white">
              <h2 className="text-lg font-bold text-slate-800">Add Medicines</h2>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, batch, manufacturer..."
                    value={searchText}
                    onChange={(e) => handleModalSearch(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-xs w-[280px] focus:outline-none focus:ring-1 focus:ring-emerald-500 focus:border-emerald-500 outline-none"
                  />
                  <Plus size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 rotate-45" />
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 hover:bg-slate-100 rounded-full transition-colors border-none bg-transparent cursor-pointer"
                >
                  <X size={20} className="text-slate-500" />
                </button>
              </div>
            </div>

            {/* Split Layout Body */}
            <div className="flex-1 flex overflow-hidden bg-slate-50/50">

              {/* Left Column Sidebar - Prescription & Patient List */}
              <div className="w-[300px] flex-shrink-0 bg-white border-r border-slate-200 overflow-y-auto p-5 space-y-6">

                {/* 1. Prescription Section */}
                <div className="space-y-3">
                  <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Prescription</h3>
                  {orderDetails.prescriptions && orderDetails.prescriptions.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {orderDetails.prescriptions.map((p, idx) => {
                        const filename = p.media
                          ? p.media.split("/").pop()?.split("?")[0] || `image${idx + 1}.jpg`
                          : `image${idx + 1}.jpg`;
                        return (
                          <a
                            key={idx}
                            href={p.media}
                            target="_blank"
                            rel="noreferrer"
                            className="flex items-center gap-1.5 px-2.5 py-1.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors text-[10px] font-bold text-slate-600 bg-white cursor-pointer no-underline"
                          >
                            <Eye size={12} className="text-slate-400 flex-shrink-0" />
                            <span className="truncate">{filename}</span>
                          </a>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-[11px] text-slate-400 italic">No prescriptions uploaded.</p>
                  )}
                </div>

                <hr className="border-slate-100" />

                {/* 2. Patient Order List Section */}
                <div className="space-y-3">
                  <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">
                    Patient Order List ({patientProducts.length} Items)
                  </h3>
                  {patientProducts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-6 text-center border-2 border-dashed border-slate-100 rounded-xl p-4 bg-slate-50/30">
                      <div className="w-12 h-12 rounded-full bg-red-50 border border-red-100 flex items-center justify-center text-red-500 mb-2">
                        <AlertCircle size={22} />
                      </div>
                      <p className="text-[10px] font-extrabold text-slate-400 max-w-[160px] leading-relaxed">
                        The patient has not selected a product or medicine.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1" style={{ scrollbarWidth: "thin" }}>
                      {patientProducts.map((p) => (
                        <div key={p.productId} className="flex justify-between items-center p-2.5 bg-slate-50 border border-slate-150 rounded-lg text-xs font-semibold">
                          <span className="font-bold text-slate-700 truncate pr-2" title={p.name}>{p.name}</span>
                          <span className="text-[9px] font-bold bg-slate-200/60 text-slate-500 px-1.5 py-0.5 rounded whitespace-nowrap flex-shrink-0">
                            Qty: {p.quantity}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Column - Available Medicines List */}
              <div className="flex-1 flex flex-col overflow-hidden p-6 bg-white">

                {/* Headers */}
                <div className="grid grid-cols-[2fr_1fr_1fr_0.8fr_1fr_1fr_0.6fr_0.6fr_0.8fr] gap-2 px-4 py-2 bg-slate-50 rounded-lg text-[10px] font-bold text-slate-400 uppercase mb-2">
                  <div>Name</div>
                  <div>Batch</div>
                  <div>Expiry</div>
                  <div>Qty/Pack</div>
                  <div>MRP</div>
                  <div>MRP/Unit</div>
                  <div className="text-center">Strips</div>
                  <div className="text-center">Tablets</div>
                  <div className="text-center">Action</div>
                </div>

                {/* Stock List Body */}
                <div className="flex-1 overflow-y-auto min-h-[350px] pr-1" style={{ scrollbarWidth: "thin" }}>
                  {modalLoading ? (
                    <div className="flex items-center justify-center py-16 text-slate-400">
                      <div className="flex flex-col items-center gap-2">
                        <Loader2 size={24} className="animate-spin text-emerald-500" />
                        <span className="text-xs">Loading stock medicines...</span>
                      </div>
                    </div>
                  ) : availableProducts.length === 0 ? (
                    <div className="flex items-center justify-center py-16 text-slate-400 text-xs">
                      No stock products found matching the query.
                    </div>
                  ) : (
                    availableProducts.map((product) => {
                      const daysLeft = getDaysRemaining(product.expiry_date);
                      const isAdded = adminProducts.some((p) => p.compositeId === product.compositeId);
                      const mrpUnit = product.mrp / (product.noOfItemsInPack || 1);

                      return (
                        <div
                          key={product.compositeId}
                          className="grid grid-cols-[2fr_1fr_1fr_0.8fr_1fr_1fr_0.6fr_0.6fr_0.8fr] gap-2 items-center px-4 py-3 border-b border-slate-50 hover:bg-slate-50/50 transition-colors text-xs font-semibold text-slate-700"
                        >
                          {/* Name */}
                          <div className="flex items-center gap-3 pr-2 min-w-0">
                            <img
                              src={product.product_image || defaultProduct}
                              alt=""
                              className="w-10 h-10 rounded object-cover flex-shrink-0 border border-slate-100"
                            />
                            <div className="min-w-0">
                              <div className="text-[12px] font-bold text-slate-800 truncate" title={product.product_name}>
                                {product.product_name}
                              </div>
                              <div className="text-[10px] text-slate-400 truncate" title={product.manufacturer_name}>
                                {product.manufacturer_name}
                              </div>
                            </div>
                          </div>

                          {/* Batch */}
                          <div className="truncate text-slate-600">{product.batch_number || "-"}</div>

                          {/* Expiry */}
                          <div>
                            <span
                              className={`text-[11px] font-bold ${daysLeft !== null && daysLeft < 30 ? "text-red-500" : "text-slate-500"
                                }`}
                            >
                              {daysLeft !== null
                                ? daysLeft > 0
                                  ? `${daysLeft} days`
                                  : "Expired"
                                : "-"}
                            </span>
                          </div>

                          {/* Qty/Pack */}
                          <div className="text-slate-500 font-semibold">{product.noOfItemsInPack}</div>

                          {/* MRP */}
                          <div className="text-slate-600 font-bold">₹ {product.mrp?.toFixed(2)}</div>

                          {/* MRP/Unit */}
                          <div className="text-slate-500 font-semibold">₹ {mrpUnit.toFixed(1)}</div>

                          {/* Strips */}
                          <div className="text-center text-slate-500">{product.remainingStocks}</div>

                          {/* Tablets */}
                          <div className="text-center text-slate-400">0</div>

                          {/* Action Add/Remove */}
                          <div className="flex justify-center">
                            <button
                              onClick={() => (isAdded ? handleRemoveProduct(product.compositeId) : handleAddProduct(product))}
                              className={`px-4 py-1.5 rounded-lg text-white text-[11px] font-bold border-none transition-all hover:shadow cursor-pointer`}
                              style={{
                                backgroundColor: isAdded ? "#DC2626" : "#10B981",
                              }}
                            >
                              {isAdded ? "Remove" : "Add"}
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Modal Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 rounded-lg mt-3">
                  <span className="text-[11px] font-bold text-slate-400">
                    Page {pagination.currentPage} of {pagination.totalPages || 1}
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const newPage = pagination.currentPage - 1;
                        if (newPage >= 1) fetchProducts(searchText, newPage, pagination.itemsPerPage);
                      }}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-1.5 text-[11px] font-bold rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => {
                        const newPage = pagination.currentPage + 1;
                        if (newPage <= (pagination.totalPages || 1))
                          fetchProducts(searchText, newPage, pagination.itemsPerPage);
                      }}
                      disabled={pagination.currentPage === (pagination.totalPages || 1)}
                      className="px-3 py-1.5 text-[11px] font-bold rounded border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <CancelOrderModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        orderId={order.orderId}
        orderNumber={order.orderNumber}
        onSuccess={onBack}
      />
    </div>
  );
};

export default PrepareOrder;
